import CompanyService from "../../../../services/CompanyService";
import { FilterCollection } from "../../../../services/FilterService";
import { PickByValue } from "../../../../utils/Utils";
import AdvancedFilters, {
  AdvancedFiltersContext
} from "../../../shared/components/AdvancedFilters";
import Dropdown from "../../../shared/components/Dropdown";
import { CheckBoxEditor } from "../../../shared/components/Editors";
import { ImportTemplateListResponse } from "../../../shared/entities/ImportTemplateListResponse";
import { TranslationService } from "../../../../services/TranslationService";
import Alarm from "./Components/Alarm";
import DateFormat from "./Components/DateFormat";
import Dictionaries from "./Components/Dictionaries";
import EditItem from "./Components/EditItem";
import ExcelOptions from "./Components/ExcelOptions";
import PrecedingProcesses from "./Components/PrecedingProcesses";
import SeparatorWithFixedColumns from "./Components/SeparatorWithFixedColumns";
import UploadTemplate from "./Components/UploadTemplate";
import ImportTemplateEditBase, {
  ImportTemplateComponentProps
} from "./ImportTemplateEditBase";
import AdvancedFilterService from "../../../../services/AdvancedFilterService";
import { useContext, useEffect, useState } from "react";
import Collapsable from "../../../shared/bootstrap/Collapsable";

const ImportTemplateEditInvoice = ({
  data,
  reload,
  importsList = undefined,
  entityId = undefined
}: {
  data?: ImportTemplateListResponse.Item;
  reload: () => void;
  importsList?: ImportTemplateListResponse;
  entityId?: number;
}) => {
  const [showDetail, setShowDetail] = useState(false);
  const requiredIds = ["contact", "createdDate", "referenceNumber", "amount"];
  const advancedFiltersContext = useContext(AdvancedFiltersContext);
  useEffect(() => {
    if (!advancedFiltersContext.show) {
      advancedFiltersContext.setShow(true);
    }
  }, [advancedFiltersContext]);

  const Header = () => {
    return (
      <div className="m-3">
        <h5 className="mb-4">
          {TranslationService.translate.SetupInvoiceImportingProcess}
        </h5>
        <details className="mt-3" onClick={() => setShowDetail((x) => !x)}>
          <summary className="ps-1 mb-2">
            <h6 style={{ display: "unset" }}>
              {TranslationService.translate.requirementsToImport}
            </h6>
          </summary>
        </details>
        <Collapsable show={showDetail}>
          {TranslationService.translate.RequirementsOfTheFileToImport.split(
            "\n"
          ).map((x, i) => (
            <div key={i} className="text-muted ms-3">
              {x}
            </div>
          ))}
        </Collapsable>
      </div>
    );
  };

  return (
    <ImportTemplateEditBase
      entityId={entityId}
      AdvancedOptions={AdvancedOptions}
      Header={Header}
      reload={reload}
      requiredFieldIds={requiredIds}
      data={data}
      showName
    />
  );
};

const AdvancedOptions = ({
  data,
  updateModel,
  model,
  setModel,
  importsList
}: ImportTemplateComponentProps) => {
  const updateNumberModel =
    <
      T extends keyof PickByValue<
        ImportTemplateListResponse.Item,
        number | undefined
      >
    >(
      key: T
    ) =>
    (value: number) => {
      setModel((x) => ({ ...x, [key]: value }));
    };

  const filtersApply = (filters: string[] | undefined) =>
    setModel((x) => ({
      ...x,
      IOFilter: AdvancedFilterService.filterStringsToQueryString(filters)
    }));

  return (
    <div>
      <small className="text-muted mb-3">
        {TranslationService.translate.IfYouLeaveFormatBlank}
      </small>
      <DateFormat
        defaultValue={data?.DateFormat}
        updateModel={updateModel("DateFormat")}
        label={TranslationService.translate.IssueDateFormat}
      />
      <DateFormat
        defaultValue={data?.DueDateFormat}
        updateModel={updateModel("DueDateFormat")}
        label={TranslationService.translate.DueDateFormat}
      />
      <CheckBoxEditor
        message={
          TranslationService.translate
            .UpdateExistingInvoiceDataWhenSynchronizing
        }
        onChange={updateModel("UpdateIOs")}
        defaultValue={data?.UpdateIOs}
      />

      {(CompanyService.isBackOfficeUser() || CompanyService.inHouse()) && (
        <>
          <div
            className="py-2 mb-3"
            style={{
              paddingLeft: 5,
              marginLeft: -5,
              backgroundColor: "#f5eebe"
            }}
          >
            <CheckBoxEditor
              message={"Actualizar datos de facturas pagadas"}
              onChange={updateModel("IncludePaid")}
              defaultValue={data?.IncludePaid}
            />
            <CheckBoxEditor
              message={"Procesar facturas con monto 0 en el archivo"}
              onChange={updateModel("processZeroIOs")}
              defaultValue={data?.processZeroIOs}
            />
          </div>
          <EditItem
            label={"En caso de factura pagada reaparecida en el archivo"}
          >
            <Dropdown
              onChange={updateNumberModel("ReOpenStrategy")}
              defaultValue={data?.ReOpenStrategy}
              items={[
                { value: 0, text: "Crear ajuste" },
                { value: 1, text: "Eliminar pagos" }
              ]}
            />
          </EditItem>
        </>
      )}

      <EditItem label={TranslationService.translate.WhenInvoiceRepeated}>
        <Dropdown
          onChange={updateNumberModel("OnRepeat")}
          defaultValue={data?.OnRepeat}
          items={[
            { value: 0, text: TranslationService.translate.DiscardFile },
            {
              value: 1,
              text: TranslationService.translate.DiscardRepeatedInvoices
            },
            {
              value: 2,
              text: TranslationService.translate.SumRepeatedInvoicesAmount
            },
            { value: 3, text: TranslationService.translate.TakeFirst },
            { value: 4, text: TranslationService.translate.TakeLast }
          ]}
        />
      </EditItem>

      {CompanyService.canEditTemplate() && (
        <UploadTemplate
          formatsAllowed=".xlsx,.csv"
          model={model}
          updateModel={updateModel}
        />
      )}

      <SeparatorWithFixedColumns
        updateModel={updateModel}
        data={data}
        model={model}
      />
      <ExcelOptions data={data} updateModel={updateModel} />

      {CompanyService.getSetting("allowimportdictionary") && (
        <>
          <hr className="my-5" />
          <Dictionaries data={data} model={model} setModel={setModel} />
        </>
      )}

      {CompanyService.getSetting("partialiosync") && (
        <>
          <hr className="my-5" />
          <h5 className="mb-3">
            {TranslationService.translate.FilterInvoices}
          </h5>
          <AdvancedFilters
            page={FilterCollection.Invoice}
            onFilterApply={filtersApply}
            defaultValue={data?.IOFilter}
            allowClosing={false}
          />
        </>
      )}

      {CompanyService.getSetting("allowimportpredecessor") && (
        <>
          <hr className="my-5" />
          <PrecedingProcesses
            data={data}
            model={model}
            setModel={setModel}
            importsList={importsList}
          />
        </>
      )}

      <hr className="my-5" />
      <Alarm
        data={data}
        updateModel={updateModel}
        setModel={setModel}
        model={model}
      />
    </div>
  );
};

export default ImportTemplateEditInvoice;
